import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { resolvePublicUploadUrl } from "../../../api/apiConfig/apiConfig";
import AuthService from "../../../api/services/AuthService";
import { listWalletDeposits } from "../../../api/services/depositDeclarationService";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import {
  extractDepositHistoryList,
  mapDepHistoryRow,
  orderHistoryClickIgnoreDetail,
  resolveDepositHistoryAddressExplorerHref,
  resolveDepositHistoryTxExplorerHref,
} from "../Order/depositHistoryShared";
import DepositHistoryDetailModal from "../Order/DepositHistoryDetailModal";
import { DepositHistoryStatus } from "../Order/DepositSenderConfirm";
import { DEPOSIT_HISTORY_REFRESH } from "../Order/depositDeclarationModel";
import "./depositPage.css";

const REQUEST_TIMEOUT_MSG = "Request timeout. Please try again.";
const NETWORK_ERROR_MSG = "Network error. Please check your connection.";

/** When API layer returns these, show `alertErrorMessage` only — not inline on the page. */
function getDepositPageAlertOnlyErrorText(msg) {
  if (msg == null) return null;
  const t = typeof msg === "string" ? msg.trim() : String(msg);
  if (t === REQUEST_TIMEOUT_MSG || /request timeout/i.test(t)) return REQUEST_TIMEOUT_MSG;
  if (t === NETWORK_ERROR_MSG) return NETWORK_ERROR_MSG;
  if (/network error/i.test(t) && /connection/i.test(t)) return NETWORK_ERROR_MSG;
  if (t === "Network Error") return NETWORK_ERROR_MSG;
  return null;
}

/**
 * Deposit page (`/asset_managemnet/deposit`):
 * - `GET /v1/wallet/deposit-coins` — coins + `chains[]` (min/max/status, names) for this UI (Bearer).
 * - `POST /v1/wallet/get-and-generate-address` — body `tokenAssetId` (+ `short_name`, `generate`); on network select: `generate: false`; on Generate click: `generate: true`.
 * - Deposit history: `GET /v1/wallet/deposits?limit=50` — recent rows below the form. Confirming the sender opens a separate screen.
 */
/**
 * Wallet APIs may return chain codes like `BSC`, `ETH`.
 * Map to internal UI chain codes (labels come from API `chain` + `chain_full_name`).
 */
const WALLET_CHAIN_TO_APP_CHAIN = {
  BSC: "BEP20",
  ETH: "ERC20",
  ERC20: "ERC20",
  BEP20: "BEP20",
  TRC20: "TRC20",
  TRON: "TRC20",
  POLYGON: "POLYGON",
  MATIC: "POLYGON",
  BTC: "BTC",
  SOL: "SOLANA",
  SOLANA: "SOLANA",
};

/**
 * `GET /v1/user/deposit-active-coins` items look like:
 * `{ _id, name, short_name, chain: string[], deposit_status: { BEP20: "ACTIVE" }, min_deposit: { BEP20: 0.5 }, max_deposit: { BEP20: 10000 }, icon_path }`
 */
function buildCoinIconUrl(coin) {
  const raw = coin?.icon_url || coin?.icon_path || coin?.icon;
  if (!raw) return null;
  return resolvePublicUploadUrl(raw) || null;
}

function CoinListThumb({ coin, size = 30 }) {
  const [idx, setIdx] = useState(0);
  const sym = (coin?.short_name || "?").toString().trim().toUpperCase();
  const candidates = useMemo(() => {
    const list = [];
    const api = buildCoinIconUrl(coin);
    if (api) list.push(api);
    return list;
  }, [coin]);

  const src = candidates[idx];
  if (!src) {
    return (
      <span className="deposit-coin-list__placeholder" style={{ width: size, height: size }} aria-hidden>
        {sym.slice(0, 2)}
      </span>
    );
  }

  return (
    <img
      className="deposit-coin-list__img"
      src={src}
      alt=""
      width={size}
      height={size}
      loading="lazy"
      decoding="async"
      onError={() => {
        if (idx < candidates.length - 1) setIdx((v) => v + 1);
        else setIdx(candidates.length); // forces placeholder
      }}
    />
  );
}

function extractCoinsList(res) {
  if (!res) return [];
  if (res.success === false) return [];
  const d = res.data;
  if (Array.isArray(d)) return d;
  if (d && Array.isArray(d.data)) return d.data;
  return [];
}

/**
 * `GET /v1/wallet/deposit-coins` can return items like:
 * {
 *   name, short_name, icon,
 *   deposit_status: "ACTIVE",
 *   chains: [{ chain: "BSC", chain_full_name, min_deposit, max_deposit, chain_deposit_status }]
 * }
 *
 * Normalize into the UI shape used throughout this page:
 * - `chain: string[]` (app chain types like BEP20/ERC20/...)
 * - `min_deposit/max_deposit/deposit_status`: maps keyed by internal chain
 * - `_chain_full_name`: map internal chain → API `chain_full_name`
 * - `_chain_api_code`: map internal chain → API `chain` (e.g. BSC, ETH) for display
 */
function normalizeWalletDepositCoins(list) {
  if (!Array.isArray(list) || list.length === 0) return [];
  const looksLikeWalletDepositCoins = list.some((x) => Array.isArray(x?.chains));
  if (!looksLikeWalletDepositCoins) return list;

  return list
    .map((coin) => {
      if (!coin || typeof coin !== "object") return null;
      const chains = Array.isArray(coin.chains) ? coin.chains : [];
      const out = {
        ...coin,
        chain: [],
        min_deposit: {},
        max_deposit: {},
        deposit_status: {},
        _chain_full_name: {},
        _chain_api_code: {},
        _chain_eta: {},
        _deposit_asset_id: {},
      };

      for (const ch of chains) {
        if (!ch || typeof ch !== "object") continue;
        const rawCode = String(ch.chain || "").trim().toUpperCase();
        if (!rawCode) continue;
        const mapped = WALLET_CHAIN_TO_APP_CHAIN[rawCode] || rawCode;
        const code = String(mapped).trim().toUpperCase();
        if (!code) continue;

        if (!out.chain.includes(code)) out.chain.push(code);
        if (ch.min_deposit != null && ch.min_deposit !== "") out.min_deposit[code] = ch.min_deposit;
        if (ch.max_deposit != null && ch.max_deposit !== "") out.max_deposit[code] = ch.max_deposit;

        const dep = ch.chain_deposit_status ?? ch.deposit_status ?? ch.status ?? coin.deposit_status;
        if (dep != null && String(dep).trim() !== "") out.deposit_status[code] = String(dep).toUpperCase();

        const full = ch.chain_full_name || ch.network_full_name || ch.chainFullName;
        if (full != null && String(full).trim()) out._chain_full_name[code] = String(full);
        out._chain_api_code[code] = rawCode;
        const etaRaw =
          ch.estimated_arrival ??
          ch.estimated_arrival_time ??
          ch.expected_arrival ??
          ch.expected_unlock ??
          ch.eta ??
          ch.confirmations_hint;
        if (etaRaw != null && String(etaRaw).trim() !== "") out._chain_eta[code] = String(etaRaw).trim();
        const assetKey =
          ch.tokenAssetId ??
          ch.token_asset_id ??
          ch.asset_id ??
          ch.assetId ??
          ch.currency_asset_id ??
          ch.deposit_asset_id;
        if (assetKey != null && String(assetKey).trim() !== "") {
          out._deposit_asset_id[code] = String(assetKey).trim();
        }
      }

      // Ensure we don't accidentally hide chains if status map wasn't included.
      for (const code of out.chain) {
        if (!out.deposit_status[code]) out.deposit_status[code] = "ACTIVE";
      }

      return out;
    })
    .filter(Boolean);
}

/**
 * `GET /v1/wallet/fireblocks-deposit-coins` can return per-chain rows:
 * { currency_id, short_name, name, chain: "BEP20", limits: {min_deposit, max_deposit, ...}, status: { deposit: "ACTIVE" } }
 * UI expects per-coin objects with `chain: string[]` + `{ min_deposit: { [chain]: n }, max_deposit: {..}, deposit_status: {..} }`.
 */
function normalizeFireblocksDepositCoins(list) {
  if (!Array.isArray(list) || list.length === 0) return [];
  // If this is the wallet `deposit-coins` format, normalize it first.
  const maybeWallet = normalizeWalletDepositCoins(list);
  if (maybeWallet !== list) return maybeWallet;
  const looksLikePerChain = list.some((x) => x && typeof x === "object" && typeof x.chain === "string" && x.currency_id);
  if (!looksLikePerChain) return list;

  const byId = new Map();
  for (const row of list) {
    if (!row || typeof row !== "object") continue;
    const id = row.currency_id || row._id || row.id;
    const chain = String(row.chain || "").trim().toUpperCase();
    if (!id || !chain) continue;

    const prev = byId.get(id) || {
      _id: id,
      currency_id: id,
      name: row.name,
      short_name: row.short_name,
      icon_path: row.icon_path,
      icon_url: row.icon_url,
      chain: [],
      min_deposit: {},
      max_deposit: {},
      deposit_status: {},
      _deposit_asset_id: {},
      // keep original rows for debugging/extra fields if needed later
      _rows: [],
    };

    prev.name = prev.name || row.name;
    prev.short_name = prev.short_name || row.short_name;
    prev.icon_url = prev.icon_url || row.icon_url;
    prev.icon_path = prev.icon_path || row.icon_path;

    if (!prev.chain.includes(chain)) prev.chain.push(chain);

    prev._chain_api_code = prev._chain_api_code || {};
    prev._chain_api_code[chain] = chain;
    prev._chain_full_name = prev._chain_full_name || {};
    prev._chain_eta = prev._chain_eta || {};

    const lim = row.limits || {};
    if (lim.min_deposit != null) prev.min_deposit[chain] = lim.min_deposit;
    if (lim.max_deposit != null) prev.max_deposit[chain] = lim.max_deposit;

    const st = row.status || {};
    const depStatus = st.deposit || row.deposit || row.deposit_status?.[chain] || row.status?.[chain];
    if (depStatus != null && String(depStatus).trim() !== "") {
      prev.deposit_status[chain] = String(depStatus).toUpperCase();
    } else {
      // default to ACTIVE if unspecified, to avoid hiding valid chains
      prev.deposit_status[chain] = "ACTIVE";
    }

    const rowAsset =
      row.tokenAssetId ??
      row.token_asset_id ??
      row.asset_id ??
      row.assetId ??
      row.currency_asset_id ??
      row.deposit_asset_id;
    if (rowAsset != null && String(rowAsset).trim() !== "") {
      prev._deposit_asset_id = prev._deposit_asset_id || {};
      prev._deposit_asset_id[chain] = String(rowAsset).trim();
    }

    prev._rows.push(row);
    byId.set(id, prev);
  }

  return Array.from(byId.values());
}

/** Wallet deposit-address responses may be a string or `{ address, memo?, tag?, ... }`. */
function parseGenerateAddressData(res) {
  const rootData = res?.data;
  const d =
    rootData && typeof rootData === "object" && !Array.isArray(rootData) && rootData.data != null
      ? rootData.data
      : rootData;
  if (d == null) return { address: "", memo: "" };
  if (typeof d === "string") return { address: d, memo: "" };
  if (typeof d === "object") {
    const raw =
      d.address ||
      d.depositAddress ||
      d.deposit_address ||
      d.wallet_address ||
      d.walletAddress ||
      d.base58Address ||
      d.base58_address ||
      d.tronAddress ||
      d.tron_address ||
      d.receiveAddress ||
      d.receive_address ||
      "";
    const mem = [d.memo, d.tag, d.destinationTag, d.destination_tag, d.memoTag].find(
      (x) => x != null && String(x).length > 0
    );
    return { address: String(raw), memo: mem != null ? String(mem) : "" };
  }
  return { address: "", memo: "" };
}

function isChainDepositActive(coin, chainCode) {
  const s = (coin.deposit_status && coin.deposit_status[chainCode]) || "";
  return String(s).toUpperCase() === "ACTIVE";
}

function normalizeCoinChains(coin) {
  const raw = coin?.chain;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (!t) return [];
    // supports: "BEP20,ERC20" or "BEP20|ERC20" or "BEP20 ERC20"
    return t.split(/[\s,|/]+/).map((x) => x.trim()).filter(Boolean);
  }
  // some backends send `{ 0: "BEP20", 1: "ERC20" }`
  if (raw && typeof raw === "object") {
    const vals = Object.values(raw).map((x) => String(x || "").trim()).filter(Boolean);
    if (vals.length) return vals;
  }
  return [];
}

/** Coin list 2nd column: value only, no “Min:” label (e.g. `0.5 USDT` or `0 ATOM`). */
function coinTableMinHint(coin) {
  const chains = normalizeCoinChains(coin);
  if (!chains.length) return "—";
  const minD = coin.min_deposit || {};
  for (let i = 0; i < chains.length; i += 1) {
    const code = chains[i];
    if (isChainDepositActive(coin, code)) {
      const v = minD[code];
      if (v != null && v !== "") {
        return `${v} ${coin.short_name || ""}`.trim();
      }
    }
  }
  return "—";
}

function getActiveNetworks(coin) {
  const chains = normalizeCoinChains(coin);
  if (!chains.length) return [];
  const minD = coin.min_deposit || {};
  const maxD = coin.max_deposit || {};
  return chains
    .filter((c) => isChainDepositActive(coin, c))
    .map((code) => {
      const minRaw = minD[code];
      const minDeposit = minRaw == null || minRaw === "" ? "—" : String(minRaw);
      const maxRaw = maxD[code];
      const maxDeposit = maxRaw != null && maxRaw !== "" ? String(maxRaw) : "—";
      const fullName = (coin?._chain_full_name?.[code] || "").trim();
      const apiChain = (coin?._chain_api_code?.[code] || code).toString().trim();
      const eta = (coin?._chain_eta?.[code] || "").trim();
      return {
        code,
        apiChain,
        name: fullName || apiChain,
        blockchainDisplay: fullName,
        minDeposit,
        maxDeposit,
        eta,
      };
    });
}

/**
 * `tokenAssetId` for `POST /v1/wallet/get-and-generate-address` — e.g. `USDT_TRX`, `BOTANIX_ETH`.
 * Prefer API `tokenAssetId` from deposit-coins chains; else `{SHORT_NAME}_{API_CHAIN}`.
 */
function buildDepositAssetId(coin, networkCode) {
  if (!coin || !networkCode) return "";
  const code = String(networkCode).trim().toUpperCase();
  const explicit = coin._deposit_asset_id?.[code];
  if (explicit != null && String(explicit).trim() !== "") return String(explicit).trim();
  const sym = String(coin.short_name || "").trim().toUpperCase();
  const apiChain = String(coin._chain_api_code?.[code] || code).trim().toUpperCase();
  if (!sym || !apiChain) return "";
  return `${sym}_${apiChain}`;
}

/** Cache deposit address per coin+network (not coin alone). */
function depositAddressCacheKey(shortName, networkCode) {
  return `${String(shortName || "").toUpperCase()}::${String(networkCode || "").toUpperCase()}`;
}

/** Dev: log when in-memory deposit address cache is written (`addressCacheRef`). */
function logDepositAddressCacheSave(cacheKey, entry, source) {
}

/** Map thrown axios / network errors to a user-visible string (ApiCallPost usually returns instead of throwing). */
function classifyDepositAddressRequestError(err) {
  if (err == null) return "Could not reach deposit service.";
  if (err.code === "ECONNABORTED" || /timeout/i.test(String(err.message || ""))) return REQUEST_TIMEOUT_MSG;
  if (!err.response) return NETWORK_ERROR_MSG;
  const status = err.response?.status;
  if (status === 401 || status === 403) return "Session expired or unauthorized. Please sign in again.";
  const apiMsg = err.response?.data?.message;
  if (apiMsg != null && String(apiMsg).trim()) return String(apiMsg).trim();
  return err.message && String(err.message).trim() ? String(err.message) : "Could not reach deposit service.";
}

/**
 * @param {boolean} generateFlag - sent as `generate` on `POST /v1/wallet/get-and-generate-address`.
 * @returns {{ res: any, parsed: { address: string, memo: string }, errorMessage: string | null }}
 */
async function requestGenerateAddressWithFallback(generateFlag, shortName, depositAssetId) {
  const assetKey = depositAssetId != null ? String(depositAssetId).trim() : "";
  if (!assetKey) {
    return { res: null, parsed: { address: "", memo: "" }, errorMessage: "Invalid deposit asset for this network." };
  }
  try {
    const res = await AuthService.getAndGenerateAddress(
      assetKey,
      shortName != null ? String(shortName) : "",
      generateFlag
    );
    if (res == null) {
      return { res: null, parsed: { address: "", memo: "" }, errorMessage: "No response from server." };
    }
    const parsed = parseGenerateAddressData(res);
    if (res && res.success === false && res.message) {
      const alertOnly = getDepositPageAlertOnlyErrorText(res.message);
      return { res, parsed, errorMessage: alertOnly || String(res.message) };
    }
    return { res, parsed, errorMessage: null };
  } catch (e) {
    return { res: null, parsed: { address: "", memo: "" }, errorMessage: classifyDepositAddressRequestError(e) };
  }
}

/** Icon for quick-pick strip — monogram if no path or load error (e.g. ATOM without icon). */
function CoinStripThumb({ coin }) {
  const [idx, setIdx] = useState(0);
  const sym = (coin?.short_name || "?").toString().trim().toUpperCase();
  const candidates = useMemo(() => {
    const list = [];
    const api = buildCoinIconUrl(coin);
    if (api) list.push(api);
    return list;
  }, [coin]);

  const src = candidates[idx];
  if (!src) {
    return (
      <span className="deposit-coin-chip__placeholder" aria-hidden>
        {sym.slice(0, 2)}
      </span>
    );
  }
  return (
    <img
      className="deposit-coin-chip__img"
      src={src}
      alt=""
      width={24}
      height={24}
      loading="lazy"
      decoding="async"
      onError={() => {
        if (idx < candidates.length - 1) setIdx((v) => v + 1);
        else setIdx(candidates.length); // forces placeholder
      }}
    />
  );
}

const StepBadge = ({ step, done }) => {
  if (done) {
    return (
      <span className="deposit_step_badge is-done" aria-label={`Step ${step} completed`}>
        <img src="/images/selectcheck.svg" alt="" aria-hidden="true" width="14" height="14" />
      </span>
    );
  }
  return (
    <span className="deposit_step_badge" aria-label={`Step ${step}`}>
      {step}
    </span>
  );
};

export default function DepositPage() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(max-width: 767.98px)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(max-width: 767.98px)");
    const onChange = (e) => setIsMobile(!!e.matches);
    setIsMobile(!!mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  const FaqBlock = (
    <div className="faq_container">
      <h2>FAQ</h2>
      <div className="accordion accordion-flush" id="accordionFlushExample">
        <div className="accordion-item">
          <h2 className="accordion-header">
            <button
              className="accordion-button collapsed"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#flush-collapseOne"
              aria-expanded="false"
              aria-controls="flush-collapseOne"
            >
              How do I deposit crypto on AGCX?
            </button>
          </h2>
          <div id="flush-collapseOne" className="accordion-collapse collapse" data-bs-parent="#accordionFlushExample">
            <div className="accordion-body">
              Select a coin and network, generate your Fireblocks deposit address, then send funds to that address from your external wallet. The
              network you select on AGCX must match the network you use to send—wrong network can result in loss of funds.
            </div>
          </div>
        </div>

        <div className="accordion-item">
          <h2 className="accordion-header">
            <button
              className="accordion-button collapsed"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#flush-collapseTwo"
              aria-expanded="false"
              aria-controls="flush-collapseTwo"
            >
              Deposit crypto — step by step
            </button>
          </h2>
          <div id="flush-collapseTwo" className="accordion-collapse collapse" data-bs-parent="#accordionFlushExample">
            <div className="accordion-body">
              <ul>
                <li>
                  <strong>Open Deposit</strong> — Go to <strong>Wallet</strong> → <strong>Deposit</strong>.
                </li>
                <li>
                  <strong>Select coin</strong> — From the list of active deposit assets.
                </li>
                <li>
                  <strong>Select network</strong> — e.g. BEP20, ERC20, TRC20, matching your withdrawal wallet.
                </li>
                <li>
                  <strong>Generate address</strong> — Creates your unique receiving address.
                </li>
                <li>
                  <strong>Send &amp; wait</strong> — Funds credit after the chain confirms; refresh balance or check history.
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="accordion-item">
          <h2 className="accordion-header">
            <button
              className="accordion-button collapsed"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#flush-collapseThree"
              aria-expanded="false"
              aria-controls="flush-collapseThree"
            >
              My deposit hasn&apos;t arrived — what should I do?
            </button>
          </h2>
          <div id="flush-collapseThree" className="accordion-collapse collapse" data-bs-parent="#accordionFlushExample">
            <div className="accordion-body">
              <ul>
                <li>
                  <strong>Check Tx</strong> — On a block explorer, confirm the transaction is successful.
                </li>
                <li>
                  <strong>Match network</strong> — The sending network must be the one you selected on AGCX.
                </li>
                <li>
                  <strong>Correct address</strong> — Re-check the full deposit address.
                </li>
                <li>
                  <strong>Wait</strong> — Congestion can delay confirmations.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
  const stepperWrapRef = useRef(null);
  const [railDims, setRailDims] = useState({ top: 0, height: 0, fill: 0 });
  const [coins, setCoins] = useState([]);
  const [loadCoinsError, setLoadCoinsError] = useState(null);
  const [loadingCoins, setLoadingCoins] = useState(true);
  const [selectedCoin, setSelectedCoin] = useState(null);
  const [selectedNetwork, setSelectedNetwork] = useState("");
  const [selectedNetworkMeta, setSelectedNetworkMeta] = useState(null);
  const [selectNetworkModalOpen, setSelectNetworkModalOpen] = useState(false);
  const [networkSearch, setNetworkSearch] = useState("");
  const [coinSearch, setCoinSearch] = useState("");
  const [isDepositDetailsOpen, setIsDepositDetailsOpen] = useState(true);
  const [depositAddress, setDepositAddress] = useState("");
  const [depositMemo, setDepositMemo] = useState("");
  const [generating, setGenerating] = useState(false);
  const [isResolvingAddress, setIsResolvingAddress] = useState(false);
  const [depositErrorUi, setDepositErrorUi] = useState({
    coin: false,
    network: false,
    address: false,
  });
  /** Per coin+network deposit address cache (see `depositAddressCacheKey`). */
  const addressCacheRef = React.useRef(new Map());
  const [depositHistoryRows, setDepositHistoryRows] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [historyError, setHistoryError] = useState(null);
  /** Same `DepositHistoryDetailModal` as Orders → Deposit History (row is not the deposit form itself). */
  const [depositHistDetailRow, setDepositHistDetailRow] = useState(null);
  const [depositHistDetailOpen, setDepositHistDetailOpen] = useState(false);

  const isStep1Done = Boolean(selectedCoin);
  const isStep2Done = Boolean(selectedNetwork);
  /** Step 3 “Deposit Details” only when we have an address and are not resolving (avoids stale QR on network change). */
  const depositDetailsReady =
    String(depositAddress || "").trim().length > 0 && !isResolvingAddress;
  // Vertical rail fill: between 3 steps we have 2 segments (0%, 50%, 100%).
  // Clamp the rail to visible step badges (fix: line should end at step 3 badge).
  useLayoutEffect(() => {
    const el = stepperWrapRef.current;
    if (!el) return;

    const compute = () => {
      try {
        const badges = Array.from(el.querySelectorAll(".deposit_step_badge"));
        if (badges.length < 2) {
          setRailDims({ top: 0, height: 0, fill: 0 });
          return;
        }
        const wrapRect = el.getBoundingClientRect();
        const firstRect = badges[0].getBoundingClientRect();
        const lastRect = badges[badges.length - 1].getBoundingClientRect();
        const firstCenter = firstRect.top - wrapRect.top + firstRect.height / 2;
        const lastCenter = lastRect.top - wrapRect.top + lastRect.height / 2;
        const top = Math.max(0, Math.round(firstCenter));
        const height = Math.max(0, Math.round(lastCenter - firstCenter));
        // Fill: step1 done -> reach step2 center, step2 done -> reach step3 center.
        const targetIdx = depositDetailsReady || isStep2Done ? badges.length - 1 : isStep1Done ? 1 : 0;
        const targetRect = badges[Math.min(Math.max(targetIdx, 0), badges.length - 1)].getBoundingClientRect();
        const targetCenter = targetRect.top - wrapRect.top + targetRect.height / 2;
        const fill = Math.max(0, Math.round(targetCenter - firstCenter));
        setRailDims({ top, height, fill });
      } catch {
        // ignore
      }
    };

    const raf = window.requestAnimationFrame(compute);
    let ro = null;
    try {
      ro = new ResizeObserver(() => compute());
      ro.observe(el);
    } catch {
      /* ignore */
    }
    window.addEventListener("resize", compute);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", compute);
      try {
        ro?.disconnect?.();
      } catch {
        /* ignore */
      }
    };
  }, [
    selectedCoin,
    selectedNetwork,
    depositDetailsReady,
    isStep1Done,
    isStep2Done,
    loadingCoins,
    isDepositDetailsOpen,
    selectNetworkModalOpen,
    isResolvingAddress,
  ]);

  const activeNetworks = useMemo(() => {
    return selectedCoin ? getActiveNetworks(selectedCoin) : [];
  }, [selectedCoin]);

  // Networks now come from `deposit-coins` response (`coin.chains`) normalized in `getActiveNetworks`.

  const filteredNetworks = useMemo(() => {
    const q = networkSearch.trim().toLowerCase();
    if (!q) return activeNetworks;
    return activeNetworks.filter(
      (n) =>
        n.code.toLowerCase().includes(q) ||
        (n.apiChain && String(n.apiChain).toLowerCase().includes(q)) ||
        (n.name && n.name.toLowerCase().includes(q)) ||
        (n.blockchainDisplay && String(n.blockchainDisplay).toLowerCase().includes(q))
    );
  }, [activeNetworks, networkSearch]);

  const filteredCoins = useMemo(() => {
    const q = coinSearch.trim().toLowerCase();
    if (!q) return coins;
    return coins.filter(
      (c) =>
        (c.short_name && String(c.short_name).toLowerCase().includes(q)) ||
        (c.name && String(c.name).toLowerCase().includes(q))
        || (c.icon_path && String(c.icon_path).toLowerCase().includes(q))
        || (c.icon && String(c.icon).toLowerCase().includes(q))
    );
  }, [coins, coinSearch]);

  const coinListDisplay = useMemo(() => filteredCoins, [filteredCoins]);

  const minForNetwork = useMemo(() => {
    if (!selectedCoin || !selectedNetwork) return "—";
    const m = selectedCoin.min_deposit;
    const v = m && m[selectedNetwork];
    if (v == null || v === "") return "—";
    return `${v} ${selectedCoin.short_name || ""}`.trim();
  }, [selectedCoin, selectedNetwork]);

  const maxForNetwork = useMemo(() => {
    if (!selectedCoin || !selectedNetwork) return "—";
    const m = selectedCoin.max_deposit;
    const v = m && m[selectedNetwork];
    if (v == null || v === "") return "—";
    return `${v} ${selectedCoin.short_name || ""}`.trim();
  }, [selectedCoin, selectedNetwork]);

  const loadCoins = useCallback(async () => {
    setLoadingCoins(true);
    setLoadCoinsError(null);
    try {
      // Deposit UI should use Fireblocks-tagged list (Bearer).
      // Fallback to `deposit-active-coins` only if the endpoint is unavailable.
      let res = null;
      let list = [];
      try {
        res = await AuthService.getDepositCoins();
        list = normalizeFireblocksDepositCoins(extractCoinsList(res));
      } catch {
        /* fall back */
      }
      if (!list.length) {
        res = await AuthService.depositActiveCoins();
        list = extractCoinsList(res);
      }
      setCoins(list);
      if (res && res.success === false) {
        const msg = res.message || "Could not load deposit options.";
        const alertText = getDepositPageAlertOnlyErrorText(msg);
        if (alertText) {
          alertErrorMessage(alertText);
        } else {
          setLoadCoinsError(msg);
        }
      }
    } catch (e) {
      const msg = e?.message || "Could not load deposit options.";
      const alertText = getDepositPageAlertOnlyErrorText(msg);
      if (alertText) {
        alertErrorMessage(alertText);
      } else {
        setLoadCoinsError(msg);
      }
    } finally {
      setLoadingCoins(false);
    }
  }, []);

  const loadDepositHistory = useCallback(async () => {
    setLoadingHistory(true);
    setHistoryError(null);
    try {
      const res = await listWalletDeposits();
      const list = extractDepositHistoryList(res);
      setDepositHistoryRows(list.map((row, i) => mapDepHistoryRow(row, i)));
      if (res && res.success === false) {
        const msg = res.message || "Could not load deposit history.";
        const alertText = getDepositPageAlertOnlyErrorText(msg);
        if (alertText) {
          alertErrorMessage(alertText);
        } else {
          setHistoryError(msg);
        }
      }
    } catch (e) {
      const msg = e?.message || "Could not load deposit history.";
      const alertText = getDepositPageAlertOnlyErrorText(msg);
      if (alertText) {
        alertErrorMessage(alertText);
      } else {
        setHistoryError(msg);
      }
      setDepositHistoryRows([]);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  const openDepositHistDetail = useCallback((row) => {
    setDepositHistDetailRow(row);
    setDepositHistDetailOpen(true);
  }, []);

  const closeDepositHistDetail = useCallback(() => {
    setDepositHistDetailOpen(false);
    setDepositHistDetailRow(null);
  }, []);

  useEffect(() => {
    void loadCoins();
  }, [loadCoins]);

  useEffect(() => {
    void loadDepositHistory();
  }, [loadDepositHistory]);

  useEffect(() => {
    const onRefresh = () => {
      void loadDepositHistory();
    };
    window.addEventListener(DEPOSIT_HISTORY_REFRESH, onRefresh);
    return () => window.removeEventListener(DEPOSIT_HISTORY_REFRESH, onRefresh);
  }, [loadDepositHistory]);

  /** `POST /v1/wallet/get-and-generate-address` when coin + network are chosen — `generate: false` (existing only). No success toast here (manual Generate only). */
  useEffect(() => {
    if (loadingCoins) return undefined;
    if (!selectedCoin?.short_name || !selectedNetwork) return undefined;

    const sym = String(selectedCoin.short_name).toUpperCase();
    const cacheKey = depositAddressCacheKey(sym, selectedNetwork);
    const cached = addressCacheRef.current.get(cacheKey);
    if (cached?.address) {
      setDepositAddress(cached.address);
      setDepositMemo(cached.memo || "");
      setDepositErrorUi((s) => ({ ...s, address: false }));
      setIsResolvingAddress(false);
      return undefined;
    }

    let cancelled = false;
    setDepositAddress("");
    setDepositMemo("");
    setIsResolvingAddress(true);
    setDepositErrorUi((s) => ({ ...s, address: false }));

    void (async () => {
      try {
        const { res, parsed, errorMessage } = await requestGenerateAddressWithFallback(
          false,
          selectedCoin.short_name,
          buildDepositAssetId(selectedCoin, selectedNetwork)
        );
        if (cancelled) return;
        const { address, memo } = parsed;
        const addrStr = String(address || "").trim();
        if (res?.success && addrStr.length > 0) {
          setDepositAddress(addrStr);
          setDepositMemo(memo != null ? String(memo) : "");
          setDepositErrorUi((s) => ({ ...s, address: false }));
          const cacheEntry = { address: addrStr, memo: memo != null ? String(memo) : "" };
          addressCacheRef.current.set(cacheKey, cacheEntry);
          logDepositAddressCacheSave(cacheKey, cacheEntry, "effect-get-existing");
          void loadDepositHistory();
        } else if (res?.success) {
          /* e.g. { success: true, message: "address fetched successfully", data: null } — no address yet; user uses Generate */
          setDepositAddress("");
          setDepositMemo("");
          setDepositErrorUi((s) => ({ ...s, address: false }));
        } else {
          setDepositAddress("");
          setDepositMemo("");
          setDepositErrorUi((s) => ({ ...s, address: true }));
          const raw =
            errorMessage ||
            (res?.message ? String(res.message) : "") ||
            "Could not load deposit address for this network.";
          const alertOnly = getDepositPageAlertOnlyErrorText(raw);
          alertErrorMessage(alertOnly || raw);
        }
      } catch (e) {
        if (!cancelled) {
          setDepositAddress("");
          setDepositMemo("");
          setDepositErrorUi((s) => ({ ...s, address: true }));
          const raw = e?.message || "Could not load deposit address.";
          const alertOnly = getDepositPageAlertOnlyErrorText(raw);
          alertErrorMessage(alertOnly || raw);
        }
      } finally {
        if (!cancelled) setIsResolvingAddress(false);
      }
    })();

    return () => {
      cancelled = true;
      setIsResolvingAddress(false);
    };
  }, [selectedCoin, selectedNetwork, loadDepositHistory, loadingCoins]);

  useEffect(() => {
    if (!selectNetworkModalOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setSelectNetworkModalOpen(false);
        setNetworkSearch("");
      }
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [selectNetworkModalOpen]);

  /** `POST /v1/wallet/get-and-generate-address` with `generate: true` — create/ensure address (button click). */
  const handleGenerateAddress = useCallback(async () => {
    if (loadingCoins) return;
    if (!selectedNetwork || !selectedCoin?.short_name) {
      setDepositErrorUi((s) => ({ ...s, address: true, network: true }));
      alertErrorMessage("Select a coin and network first.");
      return;
    }
    const sym = String(selectedCoin.short_name).toUpperCase();
    const cacheKey = depositAddressCacheKey(sym, selectedNetwork);
    const cached = addressCacheRef.current.get(cacheKey);
    if (cached?.address) {
      setDepositAddress(cached.address);
      setDepositMemo(cached.memo || "");
      return;
    }
    setGenerating(true);
    try {
      const { res, parsed, errorMessage } = await requestGenerateAddressWithFallback(
        true,
        selectedCoin.short_name,
        buildDepositAssetId(selectedCoin, selectedNetwork)
      );
      const { address, memo } = parsed;
      const addrStr = String(address || "").trim();
      if (res?.success && addrStr.length > 0) {
        setDepositAddress(addrStr);
        setDepositMemo(memo != null ? String(memo) : "");
        setDepositErrorUi((s) => ({ ...s, address: false }));
        const cacheEntry = { address: addrStr, memo: memo != null ? String(memo) : "" };
        addressCacheRef.current.set(cacheKey, cacheEntry);
        logDepositAddressCacheSave(cacheKey, cacheEntry, "generate-button");
        if (res.message) alertSuccessMessage(res.message);
        void loadDepositHistory();
        return;
      }
      if (res?.success) {
        setDepositErrorUi((s) => ({ ...s, address: false }));
        if (res.message) alertSuccessMessage(res.message);
        return;
      }
      setDepositErrorUi((s) => ({ ...s, address: true }));
      const raw =
        errorMessage ||
        (res?.message ? String(res.message) : "") ||
        "Could not generate deposit address. Check Currency for this chain and short name.";
      const alertOnly = getDepositPageAlertOnlyErrorText(raw);
      alertErrorMessage(alertOnly || raw);
    } catch (e) {
      setDepositErrorUi((s) => ({ ...s, address: true }));
      const raw = e?.message || "Could not generate deposit address.";
      const alertOnly = getDepositPageAlertOnlyErrorText(raw);
      alertErrorMessage(alertOnly || raw);
    } finally {
      setGenerating(false);
    }
  }, [selectedCoin, selectedNetwork, loadDepositHistory, loadingCoins]);


  const copyAddress = useCallback(() => {
    if (!depositAddress) return;
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(depositAddress).then(() => alertSuccessMessage("Address copied"));
    }
  }, [depositAddress]);

  const copyMemo = useCallback(() => {
    const t = depositMemo && depositMemo.trim() ? depositMemo : "—";
    if (t === "—") return;
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(t).then(() => alertSuccessMessage("Memo copied"));
    }
  }, [depositMemo]);

  const copyText = useCallback((text, label = "Copied") => {
    const t = text == null ? "" : String(text);
    if (!t || t === "—") return;
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(t).then(() => alertSuccessMessage(label));
    }
  }, []);

  const onPickCoin = (c) => {
    setSelectedCoin(c);
    setSelectedNetwork("");
    setSelectedNetworkMeta(null);
    setDepositAddress("");
    setDepositMemo("");
    setDepositErrorUi({ coin: false, network: false, address: false });
    setIsResolvingAddress(false);
    setGenerating(false);
    setCoinSearch("");
  };

  return (
    <>
      <div className="dashboard_right">
        <div className="depositcrypto_lft">
          <div className="deposit_crypto_page_head">
            <h1>Deposit Crypto</h1>
            <Link to="/asset_managemnet/deposit_fiat" className="deposit_fiat_head_btn">
              Deposit Fiat
              <span className="deposit_fiat_head_arrow" aria-hidden="true">
                <img src="/images/fiatarrow.svg" alt="" aria-hidden="true" />
              </span>
            </Link>
          </div>

          <div className="deposit_crypto_block_coin">
            <div ref={stepperWrapRef} className={`deposit_crypto_left ${selectedNetwork ? "has-step3" : ""}`}>
              {loadCoinsError ? (
                <p className="text-danger small mb-2" role="alert">
                  {loadCoinsError}
                </p>
              ) : null}
              <div
                className="deposit-stepper-rail"
                aria-hidden="true"
                style={{ "--rail-top": `${railDims.top}px`, "--rail-height": `${railDims.height}px`, "--rail-fill": `${railDims.fill}px` }}
              >
                <div className="deposit-stepper-rail__fill" />
              </div>

              <div className="deposit_step_section select_coin_option select-option">
                <div className="deposit_step_header">
                  <StepBadge step={1} done={isStep1Done} />
                  <h2>Select Coin</h2>
                </div>
                {loadingCoins ? (
                  <div
                    className="deposit-options-skeleton"
                    role="status"
                    aria-busy="true"
                    aria-label="Loading deposit options"
                  >
                    <div className="deposit-options-skeleton__trigger deposit-options-skeleton__bone" />
                    <div className="deposit-options-skeleton__chips">
                      {[0, 1, 2, 3, 4].map((i) => (
                        <span key={i} className="deposit-options-skeleton__chip deposit-options-skeleton__bone" />
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="deposit-coin-pick">
                    <div
                      className={`search_icon_s deposit_select_coin_trigger${depositErrorUi.coin ? " deposit-trigger-has-error" : ""}`}
                      role="button"
                      tabIndex={0}
                      data-bs-toggle="modal"
                      data-bs-target="#search_coin"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          const btn = e.currentTarget;
                          btn?.click?.();
                        }
                      }}
                    >
                      {selectedCoin ? (
                        <>
                          <CoinStripThumb coin={selectedCoin} />
                          <span style={{ display: "inline-flex", flexDirection: "column", lineHeight: 1.05 }}>
                            <span style={{ color: "#070808", fontWeight: 600 }}>
                              {selectedCoin?.short_name}
                            </span>
                            <span style={{ color: "#99A1AF", fontSize: 13 }}>
                              {selectedCoin?.name || ""}
                            </span>
                          </span>
                        </>
                      ) : (
                        <>
                          {/* <span className="deposit-coin-chip__placeholder" aria-hidden>
                          —
                        </span> */}
                          <span style={{ color: "#99A1AF" }}>Select coin</span>
                        </>
                      )}
                      <i className="ri-arrow-down-s-line" aria-hidden style={{ marginLeft: "auto", fontSize: 20 }} />
                    </div>

                    <div
                      className="modal search_form search_coin search_form_modal_2"
                      id="search_coin"
                      tabIndex="-1"
                      aria-labelledby="search_coin_modal_title"
                    >
                      <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                          <div className="modal-header">
                            <h4 id="search_coin_modal_title">Select Coin</h4>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
                          </div>
                          <div className="modal-body">
                            <form
                              onSubmit={(e) => e.preventDefault()}
                            >
                              <input
                                type="text"
                                className="searchfield"
                                placeholder="Search coin"
                                value={coinSearch}
                                onChange={(e) => setCoinSearch(e.target.value)}
                              />
                            </form>

                            <div className="hot_trading_t">
                              <div className="table-responsive">
                                <table>
                                  <tbody>
                                    {filteredCoins.length === 0 ? (
                                      <tr>
                                        <td colSpan={2} className="text-muted py-3">
                                          No matching coins
                                        </td>
                                      </tr>
                                    ) : (
                                      coinListDisplay.map((c) => (
                                        <tr
                                          key={c._id || c.short_name}
                                          data-bs-dismiss="modal"
                                          onClick={() => onPickCoin(c)}
                                          style={{ cursor: "pointer" }}
                                        >
                                          <td>
                                            <div className="td_first">
                                              <div className="icon">
                                                <CoinListThumb coin={c} size={30} />
                                              </div>
                                              <div className="price_heading">
                                                {c.short_name} <br />
                                                <span>{c.name || ""}</span>
                                              </div>
                                            </div>
                                          </td>
                                          <td className="right_t price_tb">
                                            <div className="price_tb_inner">
                                              <span>{coinTableMinHint(c)}</span>
                                            </div>
                                          </td>
                                        </tr>
                                      ))
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="deposit-coin-strip" role="list" aria-label="Quick select coin">
                      {!loadingCoins && coins.length === 0 ? (
                        <span className="text-muted small">No coins available</span>
                      ) : null}
                      {(() => {
                        const targetSyms = ["USDT", "BTC", "BNB", "ETH", "USDC"];
                        const picks = targetSyms.map(ts => coins.find(c => (c?.short_name || "").toUpperCase() === ts)).filter(Boolean);
                        return picks.length > 0 ? picks : coins.slice(0, 5);
                      })().map((c) => {
                        const selectedSym = (selectedCoin?.short_name || "").toString().trim().toUpperCase();
                        const sym = (c?.short_name || "").toString().trim().toUpperCase();
                        const sel = Boolean(selectedSym && sym && selectedSym === sym);
                        return (
                          <button
                            key={c._id || c.short_name}
                            type="button"
                            className={`deposit-coin-chip${sel ? " is-selected" : ""}`}
                            onClick={() => onPickCoin(c)}
                            role="listitem"
                          >
                            <CoinStripThumb coin={c} />
                            <span className="deposit-coin-chip__name">{c.short_name}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div className="deposit_step_section select_network_s select-option">
                <div className="deposit_step_header">
                  <StepBadge step={2} done={isStep2Done} />
                  <h2>Select a Network</h2>
                </div>
                {loadingCoins ? (
                  <div
                    className="deposit-options-skeleton deposit-options-skeleton--network"
                    role="presentation"
                    aria-hidden="true"
                  >
                    <div className="deposit-options-skeleton__trigger deposit-options-skeleton__bone" />
                  </div>
                ) : (
                  <div
                    className={`search_icon_s deposit_select_network_trigger${depositErrorUi.network ? " deposit-trigger-has-error" : ""}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      if (!selectedCoin) {
                        setDepositErrorUi((s) => ({ ...s, coin: true, network: true }));
                        // alertErrorMessage("Select a coin first.");
                        return;
                      }
                      if (activeNetworks.length === 0) {
                        setDepositErrorUi((s) => ({ ...s, network: true }));
                        alertErrorMessage("No active deposit network for this coin.");
                        return;
                      }
                      setDepositErrorUi((s) => ({ ...s, network: false }));
                      setSelectNetworkModalOpen(true);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if (!selectedCoin) {
                          setDepositErrorUi((s) => ({ ...s, coin: true, network: true }));
                          alertErrorMessage("Select a coin first.");
                          return;
                        }
                        if (activeNetworks.length === 0) {
                          setDepositErrorUi((s) => ({ ...s, network: true }));
                          alertErrorMessage("No active deposit network for this coin.");
                          return;
                        }
                        setDepositErrorUi((s) => ({ ...s, network: false }));
                        setSelectNetworkModalOpen(true);
                      }
                    }}
                  >
                    {selectedNetwork
                      ? (() => {
                        const api = selectedNetworkMeta?.apiChain || selectedCoin?._chain_api_code?.[selectedNetwork] || "";
                        const full =
                          selectedNetworkMeta?.blockchainDisplay ||
                          selectedCoin?._chain_full_name?.[selectedNetwork] ||
                          "";
                        if (api && full) return `${api} — ${full}`;
                        if (full) return full;
                        if (api) return api;
                        return selectedNetwork;
                      })()
                      : "Select Network"}
                  </div>
                )}
              </div>

              {selectedNetwork ? (
                <div className="deposit_step_section select_network_s ">
                  {!depositDetailsReady ? (
                    <>
                      <div className="deposit_step_header">
                        <StepBadge step={3} done={false} />
                        <h2>Deposit address</h2>
                      </div>
                      <div className="coin_items_select mt-0">
                        <div className="col-sm-6 login_btn">
                          {generating || (isResolvingAddress && !loadingCoins) ? (
                            <div className="text-muted small py-2" role="status" aria-live="polite">
                              <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />
                              Loading deposit address…
                            </div>
                          ) : (
                            <input
                              type="button"
                              value="Generate deposit address"
                              disabled={loadingCoins}
                              onClick={() => void handleGenerateAddress()}
                            />
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="deposit_step_header">
                        <StepBadge step={3} done={depositDetailsReady} />
                        <h2>Deposit Details</h2>
                      </div>
                      <div className="deposit_details_card">
                        <button
                          type="button"
                          className="deposit_details_toggle"
                          onClick={() => setIsDepositDetailsOpen((v) => !v)}
                          aria-expanded={isDepositDetailsOpen}
                          aria-controls="deposit-details-grid"
                        >
                          <span className="deposit_details_label">Address</span>
                          <span className={`deposit_details_caret ${isDepositDetailsOpen ? "is-open" : ""}`} aria-hidden="true">
                            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path
                                d="M5 7.5L10 12.5L15 7.5"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                        </button>

                        {isDepositDetailsOpen ? (
                          <div id="deposit-details-grid" className="deposit_details_grid">
                            <div className="deposit_qr_box_container">
                              <div className="deposit_qr_box">
                                {depositAddress ? (
                                  <QRCodeSVG value={depositAddress} size={160} className="deposit_qr_img" includeMargin />
                                ) : null}
                              </div>
                              <div className="deposit_qr_hint">Scan to deposit</div>
                            </div>

                            <div className="deposit_details_fields">
                              <div className="deposit_field_row">
                                <div className="deposit_field_head">
                                  <span>Deposit Address</span>
                                </div>
                                <div className="deposit_field_input">
                                  <input type="text" readOnly value={depositAddress} />
                                  <button type="button" className="deposit_copy_btn" onClick={copyAddress}>
                                    <span className="deposit_copy_ico" aria-hidden>
                                      <svg viewBox="0 0 24 24" width="16" height="16" xmlns="http://www.w3.org/2000/svg">
                                        <path
                                          fillRule="evenodd"
                                          clipRule="evenodd"
                                          d="M9 3h11v13h-3V6H9V3zM4 8v13h11V8.02L4 8z"
                                          fill="currentColor"
                                        />
                                      </svg>
                                    </span>
                                    Copy
                                  </button>
                                </div>
                              </div>

                              <div className="deposit_field_row">
                                <div className="deposit_field_head">
                                  <span>Memo (Tag)</span>
                                </div>
                                <div className="deposit_field_input">
                                  <input
                                    type="text"
                                    readOnly
                                    value={depositMemo && depositMemo.trim() ? depositMemo : "—"}
                                    className={!depositMemo || !depositMemo.trim() ? "deposit_memo_input--empty" : ""}
                                  />
                                  <button
                                    type="button"
                                    className="deposit_copy_btn"
                                    onClick={copyMemo}
                                    disabled={!depositMemo || !depositMemo.trim()}
                                  >
                                    <span className="deposit_copy_ico" aria-hidden>
                                      <svg viewBox="0 0 24 24" width="16" height="16" xmlns="http://www.w3.org/2000/svg">
                                        <path
                                          fillRule="evenodd"
                                          clipRule="evenodd"
                                          d="M9 3h11v13h-3V6H9V3zM4 8v13h11V8.02L4 8z"
                                          fill="currentColor"
                                        />
                                      </svg>
                                    </span>
                                    Copy
                                  </button>
                                </div>
                              </div>

                              <div className="deposit_details_meta">
                                <div className="deposit_meta_row">
                                  <span className="deposit_meta_label">Minimum deposit</span>
                                  <span className="deposit_meta_value deposit_meta_value--strong">{minForNetwork}</span>
                                </div>
                                <div className="deposit_meta_row">
                                  <span className="deposit_meta_label">Maximum deposit</span>
                                  <span className="deposit_meta_value deposit_meta_value--strong">{maxForNetwork}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>

                    </>
                  )}
                </div>
              ) : null}
            </div>

            {!isMobile ? <div className="deposit_crypto_right">{FaqBlock}</div> : null}
          </div>

          <div className="recent_deposit_list deposit_history_section">
            <div className="deposit_history_top">
              <h4 className="deposit_history_title">Deposit History</h4>
            </div>
            <div className="deposit_history_card">
              <div className="deposit_history_table_wrap" role="region" aria-label="Deposit history table">
                {historyError ? (
                  <p className="text-muted small px-2 py-2 mb-0" role="status">
                    {historyError}
                  </p>
                ) : null}
                {loadingHistory && depositHistoryRows.length === 0 && !historyError ? (
                  <p className="text-muted small px-2 py-3 mb-0">Loading deposit history…</p>
                ) : null}
                {!loadingHistory && depositHistoryRows.length === 0 && !historyError ? (
                  <div className="deposit_history_empty" aria-live="polite">
                    <img src="/images/no-data.svg" alt="" className="deposit_history_empty_img" width={120} height={120} />
                  </div>
                ) : null}
                {depositHistoryRows.length > 0 ? (
                  <>
                    <div className="deposit_history_desktop">
                      <div className="deposit_history_head_wrap">
                        <div className="deposit_history_head" role="row">
                          {["Date", "Network", "Amount", "Deposit Wallet", "Address", "TxID", "Action"].map(
                            (label) => (
                              <div key={label} className="deposit_history_head_cell" role="columnheader">
                                {label}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                      <div className="deposit_history_body_wrap" aria-label="Recent deposits">
                        {depositHistoryRows.map((row) => {
                          const depHistAddressExplorerHref = resolveDepositHistoryAddressExplorerHref(
                            row.explorerLink,
                            row.address
                          );
                          const depHistTxExplorerHref = resolveDepositHistoryTxExplorerHref(row.explorerLink, row.txid);
                          return (
                            <div
                              key={row.id}
                              className="deposit_history_row"
                              role="row"
                              tabIndex={0}
                              style={{ cursor: "pointer" }}
                              onClick={(e) => {
                                if (orderHistoryClickIgnoreDetail(e.target)) return;
                                openDepositHistDetail(row);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  openDepositHistDetail(row);
                                }
                              }}
                            >
                              <div className="deposit_history_row_cell" title={row.date !== "—" ? row.date : undefined}>
                                {row.date}
                              </div>
                              <div
                                className="deposit_history_row_cell"
                                title={
                                  row?.chain_full_name && row.chain_full_name !== "—"
                                    ? row.chain_full_name
                                    : row?.network && row.network !== "—"
                                      ? row.network
                                      : undefined
                                }
                              >
                                {row?.chain_full_name && row.chain_full_name !== "—" ? row.chain_full_name : row.network}
                              </div>
                              <div className="deposit_history_row_cell">
                                {row?.net_amount} {row?.short_name}
                              </div>
                              <div className="deposit_history_row_cell">{row?.depositWallet}</div>

                              <div className="deposit_history_row_cell" title={row.address !== "—" ? row.address : undefined}>
                                <span className="deposit_history_trunc">{row.addressShort}</span>
                                {row.address !== "—" ? (
                                  <>
                                    <button
                                      type="button"
                                      className="deposit_history_copy_btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        copyText(row.address, "Address copied");
                                      }}
                                      aria-label="Copy address"
                                      title="Copy address"
                                    >
                                      <span aria-hidden="true" className="deposit_history_copy_ico">
                                        <svg viewBox="0 0 24 24" width="14" height="14" xmlns="http://www.w3.org/2000/svg">
                                          <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M9 3h11v13h-3V6H9V3zM4 8v13h11V8.02L4 8z"
                                            fill="currentColor"
                                          />
                                        </svg>
                                      </span>
                                    </button>
                                    {depHistAddressExplorerHref ? (
                                      <a
                                        className="deposit_history_copy_btn deposit_history_explorer_link"
                                        href={depHistAddressExplorerHref}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="View address on explorer"
                                        title="View address on explorer"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <span aria-hidden="true" className="deposit_history_copy_ico">
                                          <svg
                                            viewBox="0 0 24 24"
                                            width="14"
                                            height="14"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                          >
                                            <path
                                              stroke="currentColor"
                                              strokeWidth="1.75"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5M16.5 3H21m0 0v4.5M21 3 9 15"
                                            />
                                          </svg>
                                        </span>
                                      </a>
                                    ) : null}
                                  </>
                                ) : null}
                              </div>
                              <div className="deposit_history_row_cell" title={row.txid !== "—" ? row.txid : undefined}>
                                <span className="deposit_history_trunc">{row.txidShort}</span>
                                {row.txid !== "—" ? (
                                  <>
                                    <button
                                      type="button"
                                      className="deposit_history_copy_btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        copyText(row.txid, "TxID copied");
                                      }}
                                      aria-label="Copy TxID"
                                      title="Copy TxID"
                                    >
                                      <span aria-hidden="true" className="deposit_history_copy_ico">
                                        <svg viewBox="0 0 24 24" width="14" height="14" xmlns="http://www.w3.org/2000/svg">
                                          <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M9 3h11v13h-3V6H9V3zM4 8v13h11V8.02L4 8z"
                                            fill="currentColor"
                                          />
                                        </svg>
                                      </span>
                                    </button>
                                    {depHistTxExplorerHref ? (
                                      <a
                                        className="deposit_history_copy_btn deposit_history_explorer_link"
                                        href={depHistTxExplorerHref}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="View transaction on explorer"
                                        title="View transaction on explorer"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <span aria-hidden="true" className="deposit_history_copy_ico">
                                          <svg
                                            viewBox="0 0 24 24"
                                            width="14"
                                            height="14"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                          >
                                            <path
                                              stroke="currentColor"
                                              strokeWidth="1.75"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5M16.5 3H21m0 0v4.5M21 3 9 15"
                                            />
                                          </svg>
                                        </span>
                                      </a>
                                    ) : null}
                                  </>
                                ) : null}
                              </div>
                              <div className="deposit_history_row_cell deposit_history_row_cell--action">
                                <DepositHistoryStatus
                                  row={row}
                                  showConfirmButton={false}
                                  badgeClassName={`deposit_history_action_badge is-${row?.actionTone || "neutral"}`}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="deposit_history_mobile" aria-label="Recent deposits">
                      <div className="withdraw_cards_list">
                        {depositHistoryRows.map((row) => {
                          const networkLabel =
                            row?.chain_full_name && row.chain_full_name !== "—" ? row.chain_full_name : row.network;
                          const depHistAddressExplorerHref = resolveDepositHistoryAddressExplorerHref(
                            row.explorerLink,
                            row.address
                          );
                          const depHistTxExplorerHref = resolveDepositHistoryTxExplorerHref(row.explorerLink, row.txid);
                          return (
                            <div
                              key={row.id}
                              className="withdraw_card_item deposit_hist_mobile_card"
                              tabIndex={0}
                              style={{ cursor: "pointer" }}
                              onClick={(e) => {
                                if (orderHistoryClickIgnoreDetail(e.target)) return;
                                openDepositHistDetail(row);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  openDepositHistDetail(row);
                                }
                              }}
                            >
                              <div className="deposit_hist_mobile_top">
                                <span className="deposit_hist_mobile_date">{row.date}</span>
                                <DepositHistoryStatus
                                  row={row}
                                  showConfirmButton={false}
                                  badgeClassName={`deposit_history_action_badge is-${row.actionTone || "neutral"}`}
                                />
                              </div>
                              <div className="withdraw_card_row">
                                <span className="label">Network</span>
                                <span className="value">{networkLabel}</span>
                              </div>
                              <div className="withdraw_card_row">
                                <span className="label">Amount</span>
                                <span className="value">
                                  {row?.net_amount} {row?.short_name}
                                </span>
                              </div>
                              <div className="withdraw_card_row">
                                <span className="label">Deposit Wallet</span>
                                <span className="value">{row?.depositWallet}</span>
                              </div>
                              <div className="withdraw_card_row deposit_hist_mobile_row--actions">
                                <span className="label">Address</span>
                                <span className="value deposit_hist_mobile_value_actions">
                                  <span className="deposit_history_trunc">{row.addressShort}</span>
                                  {row.address !== "—" ? (
                                    <>
                                      <button
                                        type="button"
                                        className="deposit_history_copy_btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          copyText(row.address, "Address copied");
                                        }}
                                        aria-label="Copy address"
                                        title="Copy address"
                                      >
                                        <span aria-hidden="true" className="deposit_history_copy_ico">
                                          <svg viewBox="0 0 24 24" width="14" height="14" xmlns="http://www.w3.org/2000/svg">
                                            <path
                                              fillRule="evenodd"
                                              clipRule="evenodd"
                                              d="M9 3h11v13h-3V6H9V3zM4 8v13h11V8.02L4 8z"
                                              fill="currentColor"
                                            />
                                          </svg>
                                        </span>
                                      </button>
                                      {depHistAddressExplorerHref ? (
                                        <a
                                          className="deposit_history_copy_btn deposit_history_explorer_link"
                                          href={depHistAddressExplorerHref}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          aria-label="View address on explorer"
                                          title="View address on explorer"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <span aria-hidden="true" className="deposit_history_copy_ico">
                                            <svg
                                              viewBox="0 0 24 24"
                                              width="14"
                                              height="14"
                                              fill="none"
                                              xmlns="http://www.w3.org/2000/svg"
                                            >
                                              <path
                                                stroke="currentColor"
                                                strokeWidth="1.75"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5M16.5 3H21m0 0v4.5M21 3 9 15"
                                              />
                                            </svg>
                                          </span>
                                        </a>
                                      ) : null}
                                    </>
                                  ) : null}
                                </span>
                              </div>
                              <div className="withdraw_card_row deposit_hist_mobile_row--actions">
                                <span className="label">TxID</span>
                                <span className="value deposit_hist_mobile_value_actions">
                                  <span className="deposit_history_trunc">{row.txidShort}</span>
                                  {row.txid !== "—" ? (
                                    <>
                                      <button
                                        type="button"
                                        className="deposit_history_copy_btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          copyText(row.txid, "TxID copied");
                                        }}
                                        aria-label="Copy TxID"
                                        title="Copy TxID"
                                      >
                                        <span aria-hidden="true" className="deposit_history_copy_ico">
                                          <svg viewBox="0 0 24 24" width="14" height="14" xmlns="http://www.w3.org/2000/svg">
                                            <path
                                              fillRule="evenodd"
                                              clipRule="evenodd"
                                              d="M9 3h11v13h-3V6H9V3zM4 8v13h11V8.02L4 8z"
                                              fill="currentColor"
                                            />
                                          </svg>
                                        </span>
                                      </button>
                                      {depHistTxExplorerHref ? (
                                        <a
                                          className="deposit_history_copy_btn deposit_history_explorer_link"
                                          href={depHistTxExplorerHref}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          aria-label="View transaction on explorer"
                                          title="View transaction on explorer"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <span aria-hidden="true" className="deposit_history_copy_ico">
                                            <svg
                                              viewBox="0 0 24 24"
                                              width="14"
                                              height="14"
                                              fill="none"
                                              xmlns="http://www.w3.org/2000/svg"
                                            >
                                              <path
                                                stroke="currentColor"
                                                strokeWidth="1.75"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5M16.5 3H21m0 0v4.5M21 3 9 15"
                                              />
                                            </svg>
                                          </span>
                                        </a>
                                      ) : null}
                                    </>
                                  ) : null}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                ) : null}
              </div>

              {isMobile ? <div className="deposit_crypto_right">{FaqBlock}</div> : null}
            </div>
          </div>

        </div>

        {selectNetworkModalOpen ? (
          <div
            className="deposit_select_network_overlay"
            role="presentation"
            onClick={() => {
              setSelectNetworkModalOpen(false);
              setNetworkSearch("");
            }}
          >
            <div
              className="deposit_select_network_dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="deposit-select-network-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="deposit_select_network_head">
                <h2 id="deposit-select-network-title" className="deposit_select_network_title">
                  Select Network
                </h2>
                <button
                  type="button"
                  className="deposit_select_network_close"
                  aria-label="Close"
                  onClick={() => {
                    setSelectNetworkModalOpen(false);
                    setNetworkSearch("");
                  }}
                >
                  <span aria-hidden="true">×</span>
                </button>
              </div>
              <div className="deposit_select_network_note">
                <span className="deposit_select_network_note_ico" aria-hidden="true">
                  !
                </span>
                <p>
                  Ensure that the selected deposit network is the same as the withdrawal network.Otherwise, it may result in loss of fund
                </p>
              </div>
              <div className="deposit_select_network_search_wrap">
                <i className="ri-search-line" aria-hidden="true" />
                <input
                  type="search"
                  className="deposit_select_network_search_input"
                  placeholder="Select Network"
                  value={networkSearch}
                  onChange={(e) => setNetworkSearch(e.target.value)}
                  autoComplete="off"
                  autoFocus
                />
              </div>
              <div className="deposit_select_network_list" role="listbox" aria-label="Networks">
                {filteredNetworks.length === 0 ? (
                  <p className="deposit_select_network_empty">No network found</p>
                ) : (
                  filteredNetworks.map((n) => (
                    <button
                      key={`${n.code}-${n.apiChain || ""}`}
                      type="button"
                      role="option"
                      aria-selected={selectedNetwork === n.code}
                      className="deposit_select_network_card"
                      onClick={() => {
                        setDepositAddress("");
                        setDepositMemo("");
                        setGenerating(false);
                        setIsResolvingAddress(true);
                        setSelectedNetwork(n.code);
                        setSelectedNetworkMeta(n);
                        setDepositErrorUi((s) => ({ ...s, network: false, address: false }));
                        setSelectNetworkModalOpen(false);
                        setNetworkSearch("");
                      }}
                    >
                      <span className="deposit_select_network_card_lft">
                        <span className="deposit_select_network_code">{n.apiChain || n.code}</span>
                        <span className="deposit_select_network_name">
                          {n.blockchainDisplay ? `${n.blockchainDisplay}` : n.name}
                        </span>
                      </span>
                      <span className="deposit_select_network_card_rgt">
                        {n.eta ? <span className="deposit_select_network_eta">{n.eta}</span> : null}
                        <span className="deposit_select_network_min">
                          {n.minDeposit === "—" && n.maxDeposit === "—"
                            ? " "
                            : [n.minDeposit !== "—" ? n.minDeposit : null, n.maxDeposit !== "—" ? `Max: ${n.maxDeposit}` : null]
                              .filter(Boolean)
                              .join(" · ")}
                        </span>
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <DepositHistoryDetailModal
        open={depositHistDetailOpen}
        row={depositHistDetailRow}
        onClose={closeDepositHistDetail}
      />
    </>
  );
}
